import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import {
  ApplyButton,
  BetweenInputs,
  BetweenSep,
  ClearAllButton,
  ClearLink,
  CountControls,
  CountInput,
  DateFilterRow,
  Divider,
  FooterRow,
  PresetSelect,
  Section,
  SectionLabel,
  SingleDatePicker,
  ToggleList,
} from 'features/projects/components/project-filters-modal/project-filters-modal-styles'
import {
  DATA_TYPE_LABELS,
  PROJECT_DATA_TYPES,
  PROJECT_USAGE_VALUES,
  STATUS_FILTER_OPTIONS,
  USAGE_LABELS,
} from 'features/projects/constants'
import {
  ArchivedFilter,
  CountFilter,
  CountOp,
  DateFilter,
  DatePreset,
  DEFAULT_COUNT_FILTER,
  EMPTY_PROJECT_FILTERS,
  getActiveFilterCount,
  NONE_FILTER_VALUE,
  ProjectFilters,
} from 'features/projects/utils/project-filters'
import { PressableToggleButton } from 'src/components/ui/PressableToggleButton'
import { PressableToggleButtonGroup } from 'src/components/ui/PressableToggleButtonGroup'

export type ProjectFiltersModalProps = {
  open: boolean
  applied: ProjectFilters
  onClose: () => void
  onApply: (filters: ProjectFilters) => void
}

// ---------------------------------------------------------------------------
// DateFilterControl — inline date preset + picker
// ---------------------------------------------------------------------------

const DATE_PRESET_OPTIONS: { label: string; value: DatePreset }[] = [
  { label: 'Any time', value: 'any' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 3 months', value: 'last_3_months' },
  { label: 'Last 12 months', value: 'last_12_months' },
  { label: 'Last 3 years', value: 'last_3_years' },
  { label: 'On...', value: 'on' },
  { label: 'Before...', value: 'before' },
  { label: 'After...', value: 'after' },
  { label: 'Range...', value: 'range' },
]

type DateFilterControlProps = {
  value: DateFilter
  onChange: (f: DateFilter) => void
}

const DateFilterControl: React.FC<DateFilterControlProps> = ({
  value,
  onChange,
}) => {
  const needsSinglePicker =
    value.preset === 'on' ||
    value.preset === 'before' ||
    value.preset === 'after'
  const needsRangePicker = value.preset === 'range'

  const handlePresetChange = (preset: DatePreset): void => {
    onChange({ preset, from: null, to: null })
  }

  const handleSingleDateChange = (d: Dayjs | null): void => {
    onChange({
      ...value,
      from: d ? d.startOf('day').unix() : null,
      to: null,
    })
  }

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
  ): void => {
    onChange({
      ...value,
      from: dates?.[0] ? dates[0].startOf('day').unix() : null,
      to: dates?.[1] ? dates[1].endOf('day').unix() : null,
    })
  }

  const singleValue = value.from !== null ? dayjs.unix(value.from) : null
  const rangeFrom = value.from !== null ? dayjs.unix(value.from) : null
  const rangeTo = value.to !== null ? dayjs.unix(value.to) : null

  return (
    <DateFilterRow>
      <PresetSelect
        value={value.preset}
        onChange={handlePresetChange}
        options={DATE_PRESET_OPTIONS}
      />
      {needsSinglePicker && (
        <SingleDatePicker
          size="small"
          value={singleValue}
          onChange={handleSingleDateChange}
          format="MMM DD, YYYY"
          placeholder="Select date"
        />
      )}
      {needsRangePicker && (
        <DatePicker.RangePicker
          size="small"
          value={[rangeFrom, rangeTo]}
          onChange={handleRangeChange}
          format="MMM DD, YYYY"
          placeholder={['Start date', 'End date']}
        />
      )}
    </DateFilterRow>
  )
}

// ---------------------------------------------------------------------------
// CountFilterRow — operator segmented + input(s)
// ---------------------------------------------------------------------------

const COUNT_OP_OPTIONS: { label: string; value: CountOp }[] = [
  { label: 'Greater than', value: 'gt' },
  { label: 'Less than', value: 'lt' },
  { label: 'Between', value: 'between' },
]

type CountFilterRowProps = {
  label: string
  value: CountFilter | null
  onChange: (f: CountFilter | null) => void
}

const CountFilterRow: React.FC<CountFilterRowProps> = ({
  label,
  value,
  onChange,
}) => {
  const active = value !== null
  const draft = value ?? DEFAULT_COUNT_FILTER

  const handleOpChange = (op: CountOp): void => {
    onChange({ ...draft, op, max: op !== 'between' ? null : draft.max })
  }

  const handleMinChange = (min: number | null): void => {
    onChange({ ...draft, min })
  }

  const handleMaxChange = (max: number | null): void => {
    onChange({ ...draft, max })
  }

  const handleClear = (): void => {
    onChange(null)
  }

  return (
    <Section>
      <SectionLabel>{label}</SectionLabel>
      <CountControls>
        <PressableToggleButtonGroup<CountOp>
          value={draft.op}
          onChange={handleOpChange}
          options={COUNT_OP_OPTIONS}
        />
        {draft.op !== 'between' ? (
          <CountInput
            min={0}
            value={draft.min}
            onValueChange={handleMinChange}
            placeholder="value"
            $width={100}
          />
        ) : (
          <BetweenInputs>
            <CountInput
              min={0}
              value={draft.min}
              onValueChange={handleMinChange}
              placeholder="min"
              $width={80}
            />
            <BetweenSep>and</BetweenSep>
            <CountInput
              min={0}
              value={draft.max}
              onValueChange={handleMaxChange}
              placeholder="max"
              $width={80}
            />
          </BetweenInputs>
        )}
        {active && (
          <ClearLink type="button" onClick={handleClear}>
            Clear
          </ClearLink>
        )}
      </CountControls>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export const ProjectFiltersModal: React.FC<ProjectFiltersModalProps> = ({
  open,
  applied,
  onClose,
  onApply,
}) => {
  const [draft, setDraft] = useState<ProjectFilters>(applied)

  // Re-seed draft whenever the modal opens with the current applied state
  useEffect(() => {
    if (open) setDraft(applied)
  }, [open, applied])

  const handleApply = (): void => {
    onApply(draft)
    onClose()
  }

  const handleClearAll = (): void => {
    setDraft(EMPTY_PROJECT_FILTERS)
  }

  const draftCount = getActiveFilterCount(draft)

  const patch = <K extends keyof ProjectFilters>(
    key: K,
    val: ProjectFilters[K],
  ): void => {
    setDraft((prev) => ({ ...prev, [key]: val }))
  }

  const toggleSet = (
    key: 'data_types' | 'usage' | 'states',
    val: string,
  ): void => {
    setDraft((prev) => {
      const current = prev[key]
      const next = current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val]
      return { ...prev, [key]: next }
    })
  }

  return (
    <PopoverModal
      isOpen={open}
      title="Filter datasets"
      onClose={onClose}
      width={660}
      bodyPadding="16px 24px"
      footer={
        <FooterRow>
          <ClearAllButton type="button" onClick={handleClearAll}>
            {`Clear${draftCount > 0 ? ` (${draftCount})` : ''}`}
          </ClearAllButton>
          <ApplyButton type="button" onClick={handleApply}>
            Apply
          </ApplyButton>
        </FooterRow>
      }
      content={
        <>
          {/* ── Internal ── */}
          <Section>
            <SectionLabel>Visibility</SectionLabel>
            <PressableToggleButtonGroup<'any' | 'internal' | 'external'>
              options={[
                { label: 'Any', value: 'any' },
                { label: 'Internal', value: 'internal' },
                { label: 'External', value: 'external' },
              ]}
              value={draft.internal}
              onChange={(v) => patch('internal', v)}
            />
          </Section>

          <Divider />

          {/* ── Archived ── */}
          <Section>
            <SectionLabel>Archived</SectionLabel>
            <PressableToggleButtonGroup<ArchivedFilter>
              options={[
                { label: 'Any', value: 'any' },
                { label: 'Active', value: 'active' },
                { label: 'Archived', value: 'archived' },
              ]}
              value={draft.archived}
              onChange={(v) => patch('archived', v)}
            />
          </Section>

          <Divider />

          {/* ── Data types ── */}
          <Section>
            <SectionLabel>Data types include…</SectionLabel>
            <ToggleList>
              {PROJECT_DATA_TYPES.map((dt) => (
                <PressableToggleButton
                  key={dt}
                  pressed={draft.data_types.includes(dt)}
                  onPressedChange={() => toggleSet('data_types', dt)}
                >
                  {DATA_TYPE_LABELS[dt]}
                </PressableToggleButton>
              ))}
              <PressableToggleButton
                pressed={draft.data_types.includes(NONE_FILTER_VALUE)}
                onPressedChange={() =>
                  toggleSet('data_types', NONE_FILTER_VALUE)
                }
              >
                None
              </PressableToggleButton>
            </ToggleList>
          </Section>

          <Divider />

          {/* ── Usage ── */}
          <Section>
            <SectionLabel>Usage</SectionLabel>
            <ToggleList>
              {PROJECT_USAGE_VALUES.map((u) => (
                <PressableToggleButton
                  key={u}
                  pressed={draft.usage.includes(u)}
                  onPressedChange={() => toggleSet('usage', u)}
                >
                  {USAGE_LABELS[u]}
                </PressableToggleButton>
              ))}
              <PressableToggleButton
                pressed={draft.usage.includes(NONE_FILTER_VALUE)}
                onPressedChange={() => toggleSet('usage', NONE_FILTER_VALUE)}
              >
                None
              </PressableToggleButton>
            </ToggleList>
          </Section>

          <Divider />

          {/* ── Counts ── */}
          <CountFilterRow
            label="Brands"
            value={draft.n_brands}
            onChange={(v) => patch('n_brands', v)}
          />
          <Divider />
          <CountFilterRow
            label="Products"
            value={draft.n_products}
            onChange={(v) => patch('n_products', v)}
          />
          <Divider />
          <CountFilterRow
            label="Sources"
            value={draft.n_sources}
            onChange={(v) => patch('n_sources', v)}
          />
          <Divider />
          <CountFilterRow
            label="Child datasets"
            value={draft.n_datasets}
            onChange={(v) => patch('n_datasets', v)}
          />
          <Divider />
          <CountFilterRow
            label="Reviews"
            value={draft.n_posts}
            onChange={(v) => patch('n_posts', v)}
          />
          <Divider />
          <CountFilterRow
            label="Rating only"
            value={draft.n_ratings}
            onChange={(v) => patch('n_ratings', v)}
          />

          <Divider />

          {/* ── Processing status ── */}
          <Section>
            <SectionLabel>Processing status</SectionLabel>
            <ToggleList>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <PressableToggleButton
                  key={opt.value}
                  pressed={draft.states.includes(opt.value)}
                  onPressedChange={() => toggleSet('states', opt.value)}
                >
                  {opt.text}
                </PressableToggleButton>
              ))}
            </ToggleList>
          </Section>

          <Divider />

          {/* ── Dates ── */}
          <Section>
            <SectionLabel>Last scrape date</SectionLabel>
            <DateFilterControl
              value={draft.last_update}
              onChange={(v) => patch('last_update', v)}
            />
          </Section>

          <Divider />

          <Section>
            <SectionLabel>Last clustered record date</SectionLabel>
            <DateFilterControl
              value={draft.last_record_date}
              onChange={(v) => patch('last_record_date', v)}
            />
          </Section>
        </>
      }
    />
  )
}
